import type { SupabaseClient } from '@supabase/supabase-js';
import { remoteTable, ALL_ENTITY_TYPES } from './tables';
import { UPSERT_ORDER } from './order';

const EPOCH = '1970-01-01T00:00:00.000Z';

export interface RemoteRow {
  entityType: string;
  row: Record<string, unknown>;
}

export interface PullResult {
  rows: RemoteRow[];
  newCursors: Record<string, string>;
  isEmpty: boolean;
}

/**
 * Pull incremental por cursor.
 * - Un único cursor global (último updated_at/reviewed_at visto).
 * - Cada tabla se consulta con gt(cursor) y se pagina con limit.
 * - review_events usa reviewed_at en vez de updated_at.
 */
export async function pullRemote(
  client: SupabaseClient,
  userId: string,
  cursors: Record<string, string>,
  limit = 500
): Promise<PullResult> {
  const rows: RemoteRow[] = [];
  const newCursors = { ...cursors };

  const orderedTypes = UPSERT_ORDER.filter((t) => ALL_ENTITY_TYPES.includes(t));

  for (const entityType of orderedTypes) {
    const table = remoteTable(entityType);
    if (!table) continue;

    const timeCol = entityType === 'reviewEvent' ? 'reviewed_at' : 'updated_at';
    const cursor = cursors[entityType] || EPOCH;

    let query = client
      .from(table)
      .select('*')
      .eq('user_id', userId)
      .order(timeCol, { ascending: true })
      .limit(limit);

    if (cursor && cursor !== EPOCH) {
      query = query.gt(timeCol, cursor);
    }

    const { data, error } = await query;
    if (error) {
      throw new Error(`pull ${table}: ${error.message}`);
    }
    if (!data) continue;

    for (const row of data as Record<string, unknown>[]) {
      rows.push({ entityType, row });
      const ts = row[timeCol];
      if (typeof ts === 'string' && ts > (newCursors[entityType] || EPOCH)) {
        newCursors[entityType] = ts;
      }
    }
  }

  return { rows, newCursors, isEmpty: rows.length === 0 };
}